<template>
    <AuthenticatedLayout
        :title="__('Shopping cart')"
    >
    <div class="max-w-screen-2xl w-full mx-auto">
        <div class="grid grid-cols-6 gap-3">
            <div class="lg:col-span-4 md:col-span-3 col-span-6 flex flex-col bg-white rounded-md shadow-md  lg:p-8 p-4">
                <h2 class="border-b-2 border-red-700 pb-4 mb-4 text-xl font-bold">{{__('Shopping cart')}}</h2>
                <div class="w-full rounded-md">
                    <header
                        class="flex sm:flex-row flex-col justify-between rounded-t-md py-2 text-xs font-bold text-left text-gray-200 uppercase bg-gray-900"
                    >
                        <div class="px-3 flex items-center" v-html="__('Products')"></div>
                        <Button
                            color="red"
                            @click="removeFromCart"
                            :disabled="!selected.length"
                            class="mx-2 justify-center sm:mt-0 mt-3"
                            :class="[
                                !selected.length ? 'opacity-60 pointer-events-none select-none': '',
                                'uppercase'
                            ]"
                        >
                            {{__('Remove selected')}}
                        </Button>
                    </header>
                    <div class="border rounded-b-md border-gray-300 shadow-md">
                        <div v-for="(item, key) in products" :key="key"
                            class="grid grid-cols-1 gap-2 text-gray-700 even:bg-gray-100"
                        >
                            <div class="p-3 flex flex-wrap justify-between">
                                <div class="flex">
                                    <div class="mr-2 flex items-center justify-center">
                                        <Checkbox
                                            :value="item.id"
                                            v-model="selected"
                                        />
                                    </div>
                                    <div class="w-full flex items-center">
                                        <div class="flex items-center text-sm" >
                                            <div class="relative hidden w-8 h-8 mr-3 rounded-full md:block">
                                                <img class="object-cover w-full h-full rounded-full"
                                                    :src="item.product.options.text_engrave == true ? item.product_variation.engrave.image.url : item.product.sortedImages[0].link"
                                                    alt="" loading="lazy">
                                                <div class="absolute inset-0 rounded-full shadow-inner" aria-hidden="true"></div>
                                            </div>
                                            <div>
                                                <InertiaLink
                                                    class="font-bold hover:underline"
                                                    :href="route('product', item.product.slug)"
                                                >
                                                    <span>{{ item.product.name }}</span>
                                                </InertiaLink>
                                                <p
                                                    class="text-xs text-gray-600"
                                                    v-html="item.product_variation ? item.product_variation.name : item.product.short_description"
                                                ></p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex md:flex-row font-bold">
                                    <div class="p-3 md:pb-3 pb-0 text-sm flex items-center">
                                        <div class="flex bg-gray-100 mt-1 rounded-md overflow-hidden">
                                            <button
                                                class="focus:outline-none w-8 py-2 bg-gray-900 text-gray-200 hover:bg-gray-800"
                                                @click="ev => {
                                                    if(item.amount > 1){
                                                        item.amount--;
                                                        updateAmountProduct(item);
                                                    }
                                                }"
                                            >
                                                <SvgIcon
                                                    name="minus"
                                                    class="mx-auto"
                                                    :size="18"
                                                />
                                            </button>
                                            <div
                                                class="w-8 flex items-center justify-center border-l border-r border-gray-700 bg-gray-900 text-gray-200"
                                            >
                                                {{ item.amount }}
                                            </div>
                                            <button
                                                class="focus:outline-none w-8 py-2 bg-gray-900 text-gray-200 hover:bg-gray-800"
                                                @click="ev=> {
                                                    item.amount++;
                                                    updateAmountProduct(item)
                                                }"
                                            >
                                                <SvgIcon
                                                    name="plus"
                                                    class="mx-auto"
                                                    :size="18"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                    <div class="p-3 md:pb-3 pb-0 text-sm">
                                        <div class="bg-gray-900 px-1 rounded-md text-gray-200 text-center">{{__('Quantity')}}</div>
                                        <div
                                            class="mt-1 px-2 py-1 w-min mx-auto font-bold text-green-700 bg-green-100 rounded-full whitespace-nowrap"
                                            v-html="getPieces(item)"
                                        ></div>
                                    </div>
                                    <div class="p-3 md:pb-3 pb-0 text-sm">
                                        <div class="bg-gray-900 px-1 rounded-md text-gray-200 text-center">{{__('Total')}}</div>
                                        <div
                                            v-if="
                                                (user.coupon && user.coupon.products.find(element => element.id == item.product_id)) ||
                                                (coupon && coupon.products.find(element => element.id == item.product_id) )"
                                            class="mt-1 p-1 text-xs w-min mx-auto font-bold text-red-700 bg-red-100 rounded-full whitespace-nowrap line-through"
                                        >
                                            {{ getOriginalPrice(item) }}
                                        </div>
                                        <div
                                            class="mt-1 px-2 py-1 w-min mx-auto font-bold text-green-700 bg-green-100 rounded-full whitespace-nowrap"
                                        >
                                            {{ getTotalPerProduct(item, true, coupon) }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="grid md:grid-cols-2 grid-cols-1 gap-2 p-3 border-t-2 border-gray-300"
                                v-if="item.font || (item.pictogram && item.product.sortedImages) || item.comment || item.content || item.color"
                            >
                                <ul class="space-y-2">
                                    <li v-if="item.font">
                                        <span class="font-bold">{{ __('Font') + ': ' }}</span>
                                        <span :style="'font-family:' + item.font.family">{{item.font.name}}</span>
                                    </li>
                                    <li class="flex items-center" v-if="item.pictogram && item.product.sortedImages">
                                        <span class="font-bold">{{ __('Images') + ': ' }}</span>
                                        <img class="h-10 ml-2" :src="item.pictogram.url" />
                                    </li>
                                    <li class="flex flex-col" v-if="item.color">
                                        <span class="font-bold">{{ __('Color') + ': ' }}</span>
                                        <div class="flex items-center font-bold">
                                            <div class="h-10 w-10 mr-1 rounded-full border-2" :style="'background-color:' + item.color.color"></div>
                                            <span>{{ item.color.title }}</span>
                                        </div>
                                    </li>
                                    <li class="flex flex-col" v-if="item.comment">
                                        <span class="font-bold">{{ __('Aditional info') + ': ' }}</span>
                                        <div v-html="item.comment.replace(/\n/g, '<br />')"></div>
                                    </li>
                                </ul>
                                <div v-if="item.content">
                                    <span class="font-bold">{{ __('Contents') + ': ' }}</span>
                                    <div class="whitespace-pre">{{ item.content }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="p-5 text-center text-red-700 font-bold" v-if="!cartItem || cartItem && cartItem.length == 0">
                            {{__('Your shopping cart is empty')}}
                        </div>
                    </div>
                </div>
            </div>
            <div class="lg:col-span-2 md:col-span-3 col-span-6 flex flex-col bg-white rounded-md shadow-md lg:p-8 p-4">
                <h2 class="border-b-2 border-red-700 pb-4 mb-4 text-xl font-bold">{{__('Summary')}}</h2>
                <div class="border-2 z-10 rounded-md p-3 bg-gray-50">
                    <ul class="text-gray-700 px-2">
                        <li class="flex py-2 justify-between items-center border-b border-gray-300">
                            <div>{{ __('Products') }}</div>
                            <div class="font-semibold">{{ cartItem ? cartItem.length : 0 }}</div>
                        </li>
                        <li
                            class="flex py-2 justify-between items-center border-b border-gray-300"
                            v-if="
                                (
                                    getCountDiscount(
                                        cartItem,
                                        coupon,
                                        getTotalPrice(cartItem, coupon, shipping).discountCart) > 0 &&
                                    (coupon || user.coupon)
                                ) ||
                                getTotalPrice(cartItem, coupon, shipping).discountCart > 0"
                        >
                            <div>{{ __('Discount') }}</div>
                            <div class="font-bold text-red-700 bg-red-100 border border-red-100 px-2 rounded-full">
                                -
                                {{
                                    getPrice(
                                        getCountDiscount(
                                            cartItem,
                                            coupon,
                                            getTotalPrice(cartItem, coupon, shipping).discountCart
                                        )
                                    )
                                }}
                            </div>
                        </li>
                        <li class="flex py-2 justify-between items-center border-b border-gray-300">
                            <div>{{ __('Shipping') }}</div>
                            <div class="font-bold text-green-700 bg-green-100 border border-green-100 px-2 rounded-full">
                                {{ $page.props.locale != 'pl' && !getTotalPrice(cartItem, coupon, shipping).free_shipping ? ( '+ ' + getPrice(shipping.price ? shipping.price.value : 0) ) : __('Free') }}
                            </div>
                        </li>
                        <li class="flex py-2 justify-between items-center">
                            <div>{{ __('Total') }}</div>
                            <div class="font-bold text-green-700 bg-green-100 border border-green-100 px-2 rounded-full">
                                {{ __( getPrice( getTotalPrice(cartItem, coupon, shipping).sum ) ) }}
                            </div>
                        </li>
                    </ul>
                </div>
                <div
                    class="pt-5"
                >
                    <p class="font-bold text-gray-700 text-sm">{{ __('Do you have coupon code?') }}</p>
                    <div class='flex' v-if="!coupon && !$page.props.auth.coupon">
                        <InputLabel
                            class="w-full mr-1"
                            v-model="coupon_code"
                        />
                        <Button
                            class="whitespace-nowrap"
                            @click="checkCoupons(false)"
                        >
                            {{__('Check code')}}
                        </Button>
                    </div>
                    <div v-else class="justify-between items-center py-2 bg-gray-100 p-2 flex">
                        <span class="font-bold">{{ coupon_code }}</span>
                        <Button
                            class="whitespace-nowrap ml-2"
                            color="red"
                            @click="checkCoupons(true)"
                        >
                            {{__('Remove coupon')}}
                        </Button>
                    </div>
                </div>
                <div
                    class="pt-5 mt-5 border-t border-gray-300"
                    v-if="cartItem && cartItem.length"
                >
                    <Button
                        :href="route('checkout', { coupon: coupon ? coupon_code : null})"
                        padding="xl"
                        custom-color="bg-green-600 hover:bg-green-700"
                        class="uppercase w-full justify-center font-bold"
                    >
                        {{__('Checkout')}}
                    </Button>
                </div>
            </div>
        </div>
        </div>

        <template #footerAddon>
            <div class="flex lg:space-x-3 space-y-3 lg:flex-row flex-col">
                <div class="flex lg:flex-none flex-wrap lg:flex-col flex-row space-y-2 lg:w-32 w-full">
                    <div
                        class="lg:h-auto h-24"
                        v-for="shipping in shipping.delivery" :key="shipping.id"
                    >
                        <img class="object-contain max-w-full max-h-full mx-auto" :src="shipping.image" />
                    </div>
                </div>
                <div class="ql-snow">
                    <div class="ql-editor content" style="padding: 0 !important;" v-html="shipping.description ? shipping.description.value : ''"></div>
                </div>
            </div>
        </template>
    </AuthenticatedLayout>
</template>

<script>
import AuthenticatedLayout from '@/Layouts/Authenticated';
import SvgIcon from 'UI/SvgIcon';
import Heading from 'COMP/Heading';
import CustomSelect from 'UI/CustomSelect';
import InputLabel from 'UI/InputLabel';

import { useForm, usePage } from '@inertiajs/inertia-vue3'
import { ref } from '@vue/reactivity';
import Button from 'UI/Button';

import {
    getPrice,
    getPieces,
    getTotalPerProduct,
    pricePerItem,
    getDiscount,
    getOriginalPrice,
    getCountDiscount,
    getTotalPrice,

    isObjectEmpty,
    inArray,
    isTrue,
} from 'helper';

import { computed } from '@vue/runtime-core';
import Checkbox from 'UI/Checkbox';

import axios from 'axios';
import NProgress from 'nprogress'

export default {
    components: {
        AuthenticatedLayout,
        SvgIcon,
        Heading,
        CustomSelect,
        InputLabel,
        Button,
        Checkbox,
    },
    props: {
        auth: Object,
        loggedin: Boolean,
        cartItem: [Array, Object],
        shipping: [Array, Object],
        first_time: Boolean
    },
    setup(props) {
        const selected = ref([]);
        const user = computed(() => usePage().props.value.auth.user);

        const products = ref(props.cartItem);

        const remove_form = useForm({
            items: selected
        })

        const removeFromCart = () => {
            remove_form.post(route('remove.from.cart'), {
                preserveScroll: true,
                onSuccess: (e) => {
                    selected.value = [];
                    products.value = props.cartItem;
                },
            });
        }

        var newStyle = document.createElement('style');
        const arrFont = ref([]);

        if(props.cartItem){
            props.cartItem.forEach((el, key) => {
                if (el.font && el.font.src && !arrFont.value.includes(el.font.name)) {

                    newStyle.appendChild(document.createTextNode("\
                        @font-face {\
                            font-family: '"+el.font.family+"';\
                            src: url('/storage/fonts/"+el.font.src+"');\
                        }\
                    "));
                    arrFont.value.push(el.font.name);
                }
            })
            document.head.appendChild(newStyle);
        }
        const coupon = ref(usePage().props.value.auth.coupon);
        const coupon_code = ref(coupon.value ? coupon.value.code : '');

        function checkCoupons(remove = false){

            let items = [];
            props.cartItem.forEach(el => {
                items.push(el.product_id);
            });

            axios
            .post(route('cart.check_coupon'), {
                coupon: coupon_code.value,
                items: items,
                remove: remove
            })
            .then((response) => {

                if(response.data.error) {
                    usePage().props.value.flash.error = response.data.error;
                    coupon.value = null;
                }
                else if(!isObjectEmpty(response.data)){
                    usePage().props.value.flash.success = response.data.success;
                    coupon.value = response.data.coupon;
                }
                else {
                    coupon.value = null;
                }

                if(remove) {
                    coupon_code.value = '';
                }
            })
        }

        function updateAmountProduct(item){
            NProgress.start();

            axios
            .post(route('update-cart-amount'), {
                item: item,
            })
            .then((response) => { NProgress.done() })
        }

        const discountCart = ref(0);

        return {
            selected,
            coupon_code,
            coupon,
            removeFromCart,
            products,
            pricePerItem,
            user,

            checkCoupons,
            arrFont,

            // computed
            getTotalPrice,
            updateAmountProduct,

            getPrice,
            getPieces,
            getTotalPerProduct,
            inArray,
            getDiscount,
            getOriginalPrice,
            getCountDiscount,
            getTotalPrice,
            discountCart,
            isTrue,
        }
    },
}
</script>
